<template>
  <ValidationObserver slim v-slot="{ handleSubmit, invalid }" ref="observer">
    <form @submit.prevent="handleSubmit(submitForm)">
      <BaseLayoutSpaced relaxed>
        <BaseLayoutSpaced>
          <ValidationProvider
            v-for="field in formFieldsWithDefaults"
            :key="field.name"
            :name="field.name"
            :vid="field.name"
            :rules="field.validation"
            v-slot="{ errors, failed }"
            slim
          >
            <BaseFormGroup>
              <BaseFormLabel :for="`${_uid}-${field.name}`">
                {{ field.label }} <template v-if="field.validation.required"> * </template>
              </BaseFormLabel>
              <Component
                v-model="data[field.name]"
                :is="field.component"
                :disabled="submitting"
                v-bind="{
                  ...field.options.props,
                  ...field.options.attrs,
                }"
                :invalid="failed"
                :id="`${_uid}-${field.name}`"
              />
              <BaseFormInlineMessage v-if="failed" danger>
                {{ errors[0] }}
              </BaseFormInlineMessage>
            </BaseFormGroup>
          </ValidationProvider>
        </BaseLayoutSpaced>
        <div class="flex space-x-2 py-1 items-baseline">
          <BaseButton primary type="submit" :disabled="invalid || submitting">
            <span v-if="!submitting">Submit</span>
            <Loader v-else small inverse />
          </BaseButton>
          <BaseButton ghost type="button" @click="$emit('cancel')" :disabled="submitting">
            Cancel
          </BaseButton>
          <BaseFormInlineMessage v-if="message" danger>
            {{ message }}
          </BaseFormInlineMessage>
        </div>
      </BaseLayoutSpaced>
    </form>
  </ValidationObserver>
</template>

<script>
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

const defaultField = {
  component: null,
  label: '',
  name: '',
  options: {},
  validation: {},
};

export default {
  mixins: [veeValidateErrorsMixin],

  props: {
    defaultData: {
      type: Object,
      default: () => ({
        name: '',
        pipelineTemplateUuid: null,
      }),
    },
    submitAction: {
      type: Function,
      default: () => {
        throw new Error('missing submit-action');
      },
    },
    pipelineTemplates: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      submitting: false,
      data: { ...this.defaultData },
      message: null,
    };
  },

  computed: {
    formFields() {
      return [
        {
          component: 'BaseFormInput',
          label: 'Name',
          name: 'name',
          options: {
            attrs: {
              name: 'name',
              class: 'w-full',
            },
          },
          validation: {
            required: true,
          },
        },
        ...(this.defaultData.pipelineTemplateUuid !== undefined
          ? [
              {
                component: 'BaseFormSelect',
                label: 'Template',
                name: 'pipelineTemplateUuid',
                options: {
                  attrs: {
                    name: 'pipelineTemplateUuid',
                  },
                  props: {
                    options: this.pipelineTemplates,
                  },
                },
                validation: {
                  required: false,
                },
              },
            ]
          : []),
      ];
    },
    formFieldsWithDefaults() {
      return this.formFields.map((x) => ({ ...defaultField, ...x }));
    },
  },

  methods: {
    async submitForm() {
      const submitWithErrorHandling = this.handleFormErrors(this.submitAction);

      this.submitting = true;

      await submitWithErrorHandling(this.data);

      this.submitting = false;
    },

    handleFormErrors(fn) {
      return (...params) =>
        fn(...params)
          .then(() => {
            this.$refs.modal.doClose();
          })
          .catch(({ status, data } = {}) => {
            // do sth with the error!
            if (status && status === HTTP_UNPROCESSABLE_ENTITY) {
              this.observer = this.$refs.observer;
              this.addServerErrors(data.errors);
            }
          });
    },
  },
};
</script>
